import { EventInput } from '@fullcalendar/react';
import { useCallback } from 'react';
import { IntlShape } from 'react-intl';
import BookingEvent from '../../domain/entities/BookingEvent';
import {
  VacancyStatus,
  VacancyStatusEnum,
} from '../../domain/entities/VacancyStatus';

const intlPref = 'merchants.services.merchantFullCalendar.calendar.';

type FormatEventTitle = (
  event: BookingEvent,
  isShorten: boolean,
  shouldSummarizeTimeSlot?: boolean,
) => string;

export const useFormatEventTitle = (intl: IntlShape) => {
  const formatEventFn = useCallback<FormatEventTitle>(
    (event, isShorten, shouldSummarizeTimeSlot = false) => {
      const vacancy = event.vacancy;
      const isWaitingListProvided = event.isWaitingListProvided;
      const isRegisteredWaitingList = event.isRegisteredWaitingList;
      const full = event.full;
      let title = event.title;

      // shorten title if needed
      if (isShorten) {
        const maxTitleLength = 24;
        title =
          title.length <= maxTitleLength
            ? title
            : title.substr(0, maxTitleLength) + '...';
      }

      // add suffix to title by vacancy
      const vacancyStatus: VacancyStatus = new VacancyStatus({
        vacancyCount: vacancy,
        waitingListProvided: isWaitingListProvided,
        full,
        isRegisteredWaitingList,
      });

      let suffix = '';

      switch (vacancyStatus.vacancyAppearance) {
        case VacancyStatusEnum.AVAILABLE:
          suffix = intl.formatMessage(
            { id: intlPref + 'statusInTitle.vacancy' },
            { vacancy: vacancyStatus.vacancyCount },
          );
          break;
        case VacancyStatusEnum.WAITING_LIST:
          suffix = intl.formatMessage(
            { id: intlPref + 'statusInTitle.waitingList' },
            { vacancy: vacancyStatus.vacancyCount },
          );
          break;
        case VacancyStatusEnum.REGISTERED_WAITING_LIST:
          suffix = intl.formatMessage({
            id: intlPref + 'statusInTitle.registeredWaitingList',
          });
          break;
        default:
          break;
      }

      return title + (shouldSummarizeTimeSlot ? '' : suffix);
    },
    [intl],
  );
  return formatEventFn;
};

export const useFilterBookingPagesCalendar = (bookingPageIds?: string[]) => {
  return useCallback(
    (bookingEvents: EventInput[]) => {
      if (bookingPageIds) {
        return bookingEvents.filter((event) => {
          return bookingPageIds.includes(event.bookingPageId);
        });
      } else {
        return bookingEvents;
      }
    },
    [bookingPageIds],
  );
};

export const mapToCalendarEvents = (events: BookingEvent[]): EventInput[] => {
  return events.map((event) => {
    return {
      ...event,
      allDay: event.isAllDay,
      bookingEvent: event,
      full: event.full,
      classNames: event.classNames,
      shouldSummarizeTimeSlot: false,
    };
  });
};

// time_slotを1日毎にまとめて表示する場合に使う
export const mapToAllDayCalendarEvents = (
  events: BookingEvent[],
): EventInput[] => {
  return events.map((event) => {
    return {
      ...event,
      allDay: true,
      bookingEvent: {
        ...event,
      },
      full: event.full,
      classNames: [],
      shouldSummarizeTimeSlot: true,
    };
  });
};
